import React, { useReducer, useEffect, useState, useMemo } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Switch,
    Button,
    Box,
    IconButton,
    TextField,
    makeStyles,
    CircularProgress,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl
} from "@material-ui/core";
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import DeleteIcon from "@material-ui/icons/Delete";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import EditIcon from '@material-ui/icons/Edit';
import { orange, red } from '@material-ui/core/colors';
import axios from "axios";
import { appUrl } from "../../../../../utils/config";


const useStyles = makeStyles((theme) => ({
    tableHeader: {
        background: '#F4F6F8',
    },
    headerCell: {
        fontWeight: 700,
        background: '#F4F6F8',
        borderBottom: '2px solid #E0E0E0',
        color: '#222',
        fontSize: 16,
        padding: '12px 16px',
    },
    actionIcon: {
        color: orange[500],
        cursor: 'pointer',
        marginRight: 8,
    },
    deleteIcon: {
        color: red[500],
        cursor: 'pointer',
    },
    addButton: {
        borderRadius: 20,
        textTransform: "none",
        fontWeight: 600,
        fontSize: 16,
        padding: '6px 18px',
        marginTop: 12,
        marginBottom: 12,
        border: '1.5px solid #222',
        background: '#fff',
        color: '#222',
        '&:hover': {
            background: '#f5f5f5',
        },
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    saveButton: {
        backgroundColor: '#f26522',
        color: '#fff',
        borderRadius: '20px',
        padding: '8px 24px',
        textTransform: 'none',
        fontWeight: 600,
        marginBottom: 12,
        marginLeft: 24,
        '&:hover': {
            backgroundColor: '#d4551a',
        },
    },
    resetButton: {
        border: '1px solid #000000',
        color: '#000000',
        fontWeight: 600,
        borderRadius: '20px',
        padding: '6px 16px',
        textTransform: 'none',
        marginBottom: 12,
        marginLeft: 16,
        background: '#fff',
        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
    },
    link: {
        color: '#1890FF',
        textDecoration: 'underline',
        cursor: 'pointer',
        fontWeight: 500,
    },
    editableCell: {
        padding: '4px 8px',
    },
    vendorCell: {
        fontSize: 14,
        lineHeight: 1.3,
    },
    actionCell: {
        minWidth: 120,
    },
    pencil: {
        color: '#F5A623',
        marginLeft: 4,
        verticalAlign: 'middle',
    },
    switchBase: {
        '&.Mui-checked': {
            color: '#007bff',
        },
        '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#007bff',
        },
    },
    modalityBoxWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    containerBox: {
        flex: 1,
    },
    inlineActions: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    iconBtn: {
        padding: 4,
    },
    textField: {
        flex: 1,
        marginRight: 8,
        '& .MuiInputBase-root': {
            padding: '5px 7px',
        },
        '& .MuiInputBase-input': {
            padding: '5px 7px',
        },
    },
}));

const useDialogStyles = makeStyles((theme) => ({
    dialogTitle: {
        fontWeight: 900,
        fontSize: 32,
        textAlign: 'center',
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(1),
    },
    dialogContent: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        minHeight: 0,
    },
    scrollList: {
        maxHeight: 200,
        overflowY: 'auto',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        paddingRight: theme.spacing(1),
        scrollbarWidth: 'thin',
        scrollbarColor: '#bdbdbd #f5f5f5', 
        overscrollBehavior: 'contain',
        '&::-webkit-scrollbar': {
            width: 8,
            background: '#f5f5f5',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#bdbdbd',
            borderRadius: 8,
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: '#9e9e9e',
        },
    },
    dialogPaper: {
        minHeight: 0,
        borderRadius: 16,
    },
}));

// Transform API data to component format
const transformApiToComponent = (apiData) => {
    return apiData.map((item, index) => ({
        id: item.processFlowID?.toString() || `ID-${index + 1}`,
        name: item.processFlowName || '',
        mapping: "Click here to view",
        version: item.versionHistory && item.versionHistory.length > 0 ? "Click here to view" : "NA",
        qaCheck: item.qaCheckRequired ? "Yes" : "No",
        cmoCountry: item.cmoCountry || '',
        addresseeOrgName: item.addresseeOrgName || '',
        addresseeCountry: item.addresseeCountry || '',
        vendor: item.cmoCountry && item.addresseeOrgName && item.addresseeCountry
            ? [`${item.cmoCountry} - ${item.addresseeOrgName} - ${item.addresseeCountry}`]
            : [],
        selected: !!item.activated,
        isNew: false,
        processFlowType: item.processFlowType || 'Default',
        originalData: item, // Keep original API data for reference
    }));
};

// Transform component data back to API format
const transformComponentToApi = (componentData) => {
    return componentData.map(item => ({
        processFlowID: item.isNew ? null : parseInt(item.id),
        processFlowName: item.name,
        qaCheckRequired: item.qaCheck === "Yes",
        cmoCountry: item.cmoCountry || item.vendor[0]?.split(' - ')[0] || '',
        addresseeOrgName: item.addresseeOrgName || item.vendor[0]?.split(' - ')[1] || '',
        addresseeCountry: item.addresseeCountry || item.vendor[0]?.split(' - ')[2] || '',
        brandOwnerMosymphonyID: item.originalData?.brandOwnerMosymphonyID,
        activated: !!item.selected,
        markForDelete: false,
        processFlowType: item.originalData?.processFlowType || 'Default',
        versionHistory: item.originalData?.versionHistory || [],
    }));
};

function deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
}

function reducer(state, action) {
    switch (action.type) {
        case 'SET_PROCESS_FLOWS':
            return {
                ...state,
                processFlows: action.payload,
                originalProcessFlows: deepClone(action.payload),
                isUpdated: false,
            };
        case 'TOGGLE_SELECTION':
            const updatedFlows = [...state.processFlows];
            if (updatedFlows[action.index]) {
                updatedFlows[action.index] = {
                    ...updatedFlows[action.index],
                    selected: !updatedFlows[action.index].selected,
                    isUpdated: true, // Mark as updated for both new and existing flows
                };
            }
            return {
                ...state,
                processFlows: updatedFlows,
                isUpdated: true,
            };
        case 'ADD_NEW':
            const newFlow = {
                id: action.nextId ? action.nextId.toString() : '1',
                name: '',
                mapping: "Click here to view",
                version: "NA",
                qaCheck: '',
                cmoCountry: '',
                addresseeOrgName: '',
                addresseeCountry: '',
                vendor: [''],
                selected: false,
                isNew: true,
                isEditing: {},
            };
            return {
                ...state,
                processFlows: [...state.processFlows, newFlow],
                isUpdated: true,
            };
        case 'UPDATE_FIELD':
            const updatedFlowsWithField = [...state.processFlows];
            if (updatedFlowsWithField[action.index]) {
                if (action.field === 'vendor' && action.vIdx !== undefined) {
                    if (!updatedFlowsWithField[action.index].vendor) {
                        updatedFlowsWithField[action.index].vendor = [];
                    }
                    updatedFlowsWithField[action.index].vendor[action.vIdx] = action.value;
                } else {
                    updatedFlowsWithField[action.index][action.field] = action.value;
                }
                // Mark as updated for both new and existing flows
                updatedFlowsWithField[action.index].isUpdated = true;
            }
            return {
                ...state,
                processFlows: updatedFlowsWithField,
                isUpdated: true,
            };
        case 'SET_EDITING':
            const updatedFlowsWithEditing = [...state.processFlows];
            if (updatedFlowsWithEditing[action.index]) {
                if (!updatedFlowsWithEditing[action.index].isEditing) {
                    updatedFlowsWithEditing[action.index].isEditing = {};
                }
                if (action.field === 'vendor' && action.vIdx !== undefined) {
                    if (!updatedFlowsWithEditing[action.index].isEditing.vendor) {
                        updatedFlowsWithEditing[action.index].isEditing.vendor = {};
                    }
                    updatedFlowsWithEditing[action.index].isEditing.vendor[action.vIdx] = true;
                } else {
                    updatedFlowsWithEditing[action.index].isEditing[action.field] = true;
                }
            }
            return {
                ...state,
                processFlows: updatedFlowsWithEditing,
            };
        case 'SAVE_FIELD':
            const updatedFlowsWithSave = [...state.processFlows];
            if (updatedFlowsWithSave[action.index]) {
                if (action.field === 'vendor' && action.vIdx !== undefined) {
                    if (updatedFlowsWithSave[action.index].isEditing?.vendor) {
                        delete updatedFlowsWithSave[action.index].isEditing.vendor[action.vIdx];
                    }
                } else {
                    if (updatedFlowsWithSave[action.index].isEditing) {
                        delete updatedFlowsWithSave[action.index].isEditing[action.field];
                    }
                }
            }
            return {
                ...state,
                processFlows: updatedFlowsWithSave,
            };
        case 'CANCEL_FIELD':
            const updatedFlowsWithCancel = [...state.processFlows];
            if (updatedFlowsWithCancel[action.index]) {
                if (action.field === 'vendor' && action.vIdx !== undefined) {
                    const originalValue = state.originalProcessFlows[action.index]?.vendor?.[action.vIdx] || '';
                    if (!updatedFlowsWithCancel[action.index].vendor) {
                        updatedFlowsWithCancel[action.index].vendor = [];
                    }
                    updatedFlowsWithCancel[action.index].vendor[action.vIdx] = originalValue;
                    if (updatedFlowsWithCancel[action.index].isEditing?.vendor) {
                        delete updatedFlowsWithCancel[action.index].isEditing.vendor[action.vIdx];
                    }
                } else {
                    const originalValue = state.originalProcessFlows[action.index]?.[action.field] || '';
                    updatedFlowsWithCancel[action.index][action.field] = originalValue;
                    if (updatedFlowsWithCancel[action.index].isEditing) {
                        delete updatedFlowsWithCancel[action.index].isEditing[action.field];
                    }
                }
            }
            return {
                ...state,
                processFlows: updatedFlowsWithCancel,
            };
        case 'DELETE_ROW':
            const updatedFlowsWithDelete = state.processFlows.filter((_, index) => index !== action.index);
            return {
                ...state,
                processFlows: updatedFlowsWithDelete,
                isUpdated: true,
            };
        case 'ADD_VENDOR':
            const updatedFlowsWithVendor = [...state.processFlows];
            if (updatedFlowsWithVendor[action.index] && updatedFlowsWithVendor[action.index].vendor) {
                updatedFlowsWithVendor[action.index].vendor.push('');
            }
            return {
                ...state,
                processFlows: updatedFlowsWithVendor,
                isUpdated: true,
            };
        case 'SAVE_ALL':
            return {
                ...state,
                originalProcessFlows: deepClone(state.processFlows),
                isUpdated: false,
            };
        case 'RESET_ALL':
            return {
                ...state,
                processFlows: deepClone(state.originalProcessFlows),
                isUpdated: false,
            };
        default:
            return state;
    }
}

function hasEmptyRequiredFields(processFlows) {
    return processFlows.some(flow =>
        flow.isNew && (!flow.name?.trim() || !flow.qaCheck?.trim() || !flow.cmoCountry?.trim() || !flow.addresseeOrgName?.trim() || !flow.addresseeCountry?.trim())
    );
}

const ProcessFlowTab = ({ setProcessFlow, featuresAccess, sponsorId }) => {
    const classes = useStyles();
    const dialogClasses = useDialogStyles();
    const [state, dispatch] = useReducer(reducer, {
        processFlows: [],
        originalProcessFlows: [],
        isUpdated: false,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savingStates, setSavingStates] = useState({});
    const [errorMessage, setErrorMessage] = useState("");
    const [copyDialogOpen, setCopyDialogOpen] = useState(false);
    const [copySelected, setCopySelected] = useState('none');
    
    // Get edit access from featuresAccess
    const canEdit = featuresAccess?.editSponsorNetworkDetails ?? false;

    // API base URL
    const apiBaseUrl = useMemo(() => {
        const appName = localStorage.getItem("appName");
        const network = localStorage.getItem("network");
        const collabType = localStorage.getItem("collabType");
        const roleName = localStorage.getItem("roleName");

        if (appName && network && collabType && roleName) {
            return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
        }
        return null;
    }, [
        localStorage.getItem("appName"),
        localStorage.getItem("network"),
        localStorage.getItem("collabType"),
        localStorage.getItem("roleName"),
    ]);

    // Fetch process flows on component mount
    useEffect(() => {
        const fetchProcessFlows = async () => {
            if (!apiBaseUrl) return;

            setIsLoading(true);
            try {
                // productConfig = false for sponsor configuration UI
                const res = await axios.get(`${apiBaseUrl}/fetchAllProcessFlows/false`);
                // console.log('API Response:', res.data);
                if (res.data && res.data.status && res.data.processFlowsList) {
                    const transformedData = transformApiToComponent(res.data.processFlowsList);
                    // console.log('Transformed Data:', transformedData);
                    dispatch({ type: 'SET_PROCESS_FLOWS', payload: transformedData });
                }
            } catch (e) {
                console.error('Error fetching process flows:', e);
            } finally {
                setIsLoading(false);
            }
        };
        fetchProcessFlows();
    }, [apiBaseUrl]);

    const handleToggle = (index) => {
        dispatch({ type: 'TOGGLE_SELECTION', index });
    };

    // Helper to get only existing flows (not isNew)
    const existingFlows = state.processFlows.filter(flow => !flow.isNew);

    // Modified handleAddNew to use dialog
    const handleAddNew = () => {
        setCopyDialogOpen(true);
        setCopySelected('none');
    };

    // Add new process flow row, optionally prefilled
    const handleCopyContinue = () => {
        let newFlow = {
            id: (existingFlows.length + state.processFlows.filter(f => f.isNew).length + 1).toString(),
            name: '',
            mapping: "Click here to view",
            version: "NA",
            qaCheck: '',
            cmoCountry: '',
            addresseeOrgName: '',
            addresseeCountry: '',
            vendor: [''],
            selected: false,
            isNew: true,
            isEditing: {},
        };
        if (copySelected !== 'none') {
            const copyFrom = existingFlows.find(f => f.id === copySelected);
            if (copyFrom) {
                newFlow = {
                    ...newFlow,
                    name: 'Copy of ' + copyFrom.name,
                    qaCheck: copyFrom.qaCheck,
                    cmoCountry: copyFrom.cmoCountry,
                    addresseeOrgName: copyFrom.addresseeOrgName,
                    addresseeCountry: copyFrom.addresseeCountry,
                    vendor: [...(copyFrom.vendor || [''])],
                };
            }
        }
        dispatch({ type: 'ADD_NEW', nextId: newFlow.id });
        // Patch the last added row with prefilled data if needed
        setTimeout(() => {
            if (copySelected !== 'none') {
                const idx = state.processFlows.length; // new row index
                dispatch({ type: 'UPDATE_FIELD', index: idx, field: 'name', value: newFlow.name });
                dispatch({ type: 'UPDATE_FIELD', index: idx, field: 'qaCheck', value: newFlow.qaCheck });
                dispatch({ type: 'UPDATE_FIELD', index: idx, field: 'cmoCountry', value: newFlow.cmoCountry });
                dispatch({ type: 'UPDATE_FIELD', index: idx, field: 'addresseeOrgName', value: newFlow.addresseeOrgName });
                dispatch({ type: 'UPDATE_FIELD', index: idx, field: 'addresseeCountry', value: newFlow.addresseeCountry });
                dispatch({ type: 'UPDATE_FIELD', index: idx, field: 'vendor', value: newFlow.vendor });
            }
        }, 0);
        setCopyDialogOpen(false);
    };

    const handleFieldChange = (idx, field, value) => {
        dispatch({ type: 'UPDATE_FIELD', index: idx, field, value });
    };

    const handleVendorChange = (idx, vIdx, value) => {
        dispatch({ type: 'UPDATE_FIELD', index: idx, field: 'vendor', vIdx, value });
    };

    const handleAddVendor = (idx) => {
        dispatch({ type: 'ADD_VENDOR', idx });
    };

    const handleSetEditing = (idx, field, vIdx) => {
        dispatch({ type: 'SET_EDITING', index: idx, field, vIdx });
    };

    const handleSaveField = (idx, field, vIdx) => {
        dispatch({ type: 'SAVE_FIELD', index: idx, field, vIdx });
    };

    const handleCancelField = (idx, field, vIdx) => {
        dispatch({ type: 'CANCEL_FIELD', index: idx, field, vIdx });
    };

    const handleDeleteRow = async (idx) => {
        const processFlow = state.processFlows[idx];
        return
        
        // If it's a new row, just remove it from state
        if (processFlow.isNew) {
            dispatch({ type: 'DELETE_ROW', index: idx });
            return;
        }

        // If it's an existing row, call API to delete it
        try {
            const payload = {
                sponsorId: sponsorId,
                ecosystemId: localStorage.getItem("ecosystemId"),
                processFlowsList: [{
                    processFlowID: parseInt(processFlow.id),
                    processFlowName: processFlow.name,
                    qaCheckRequired: processFlow.qaCheck === "Yes",
                    cmoCountry: processFlow.cmoCountry,
                    addresseeOrgName: processFlow.addresseeOrgName,
                    addresseeCountry: processFlow.addresseeCountry,
                    activated: processFlow.selected,
                    processFlowType: processFlow.processFlowType || "Default",
                    markForDelete: true
                }]
            };

            const response = await axios.post(`${apiBaseUrl}/updateProcessFlows`, payload);

            if (response.data && response.data.status) {
                // console.log('Process flow deleted successfully');
                // Refresh the data after successful delete
                const fetchResponse = await axios.get(`${apiBaseUrl}/fetchAllProcessFlows/false`);
                if (fetchResponse.data && fetchResponse.data.status && fetchResponse.data.processFlowsList) {
                    const transformedData = transformApiToComponent(fetchResponse.data.processFlowsList);
                    dispatch({ type: 'SET_PROCESS_FLOWS', payload: transformedData });
                }
                // Notify parent component to update the count
                if (setProcessFlow) {
                    setProcessFlow(true);
                }
            } else {
                console.error('Failed to delete process flow:', response.data?.message);
                setErrorMessage(response.data?.message || "Failed to delete process flow");
            }
        } catch (e) {
            console.error('Error deleting process flow:', e);
            setErrorMessage("An error occurred while deleting the process flow");
        }
    };

    // Save entire row for new process flow
    const handleSaveRow = async (index) => {
        const processFlow = state.processFlows[index];

        if (!processFlow.name?.trim() || savingStates[index]) return;
        
        // Check for duplicate names
        const duplicateExists = state.processFlows.some(
            (flow, i) => i !== index && flow.name?.trim().toLowerCase() === processFlow.name?.trim().toLowerCase()
        );

        if (duplicateExists) {
            setErrorMessage("Process Flow name already exists");
            return;
        }

        // Validate QA Check field
        if (processFlow.qaCheck && !["Yes", "No"].includes(processFlow.qaCheck)) {
            setErrorMessage("QA Check Required must be 'Yes' or 'No'");
            return;
        }

        // Validate that all required fields are filled
        if (!processFlow.name?.trim() || !processFlow.qaCheck?.trim() || 
            !processFlow.cmoCountry?.trim() || !processFlow.addresseeOrgName?.trim() || 
            !processFlow.addresseeCountry?.trim()) {
            setErrorMessage("All fields are required");
            return;
        }

        setSavingStates(prev => ({ ...prev, [index]: true }));
        setErrorMessage("");

        try {
            // Fetch latest data to get the true maximum ID from the database
            const fetchResponse = await axios.get(`${apiBaseUrl}/fetchAllProcessFlows/false`);
            let maxId = 0;
            
            if (fetchResponse.data && fetchResponse.data.status && fetchResponse.data.processFlowsList) {
                // Get the maximum ID from the database
                const dbIds = fetchResponse.data.processFlowsList
                    .map(item => {
                        const id = parseInt(item.processFlowID);
                        return isNaN(id) ? 0 : id;
                    });
                
                maxId = dbIds.length > 0 ? Math.max(...dbIds) : 0;
            }
            
            // Generate next ID as maxId + 1
            const nextId = maxId + 1;

            const payload = {
                sponsorId: sponsorId,
                ecosystemId: localStorage.getItem("ecosystemId"),
                processFlowsList: [{
                    processFlowID: nextId,
                    processFlowName: processFlow.name,
                    qaCheckRequired: processFlow.qaCheck === "Yes",
                    cmoCountry: processFlow.cmoCountry,
                    addresseeOrgName: processFlow.addresseeOrgName,
                    addresseeCountry: processFlow.addresseeCountry,
                    activated: processFlow.selected,
                    processFlowType: "New"
                }]
            };

            // console.log('Saving row payload:', payload);

            const response = await axios.post(`${apiBaseUrl}/addProcessFlows`, payload);

            if (response.data && response.data.status) {
                // console.log('Row saved successfully');
                // Refresh the data after successful save
                const fetchResponse = await axios.get(`${apiBaseUrl}/fetchAllProcessFlows/false`);
                if (fetchResponse.data && fetchResponse.data.status && fetchResponse.data.processFlowsList) {
                    const transformedData = transformApiToComponent(fetchResponse.data.processFlowsList);
                    dispatch({ type: 'SET_PROCESS_FLOWS', payload: transformedData });
                }
                // Notify parent component to update the count
                if (setProcessFlow) {
                    setProcessFlow(true);
                }
            } else {
                console.error('Failed to save row:', response.data?.message);
                setErrorMessage(response.data?.message || "Failed to save row");
            }
        } catch (e) {
            console.error('Error saving row:', e);
            setErrorMessage("An error occurred while saving the row");
        } finally {
            setSavingStates(prev => ({ ...prev, [index]: false }));
        }
    };

    const handleSaveAll = async () => {
        if (!apiBaseUrl) return;

        setSaving(true);
        try {
            // Get process flows that have changes (existing flows that were modified)
            const changedProcessFlows = state.processFlows.filter(flow => !flow.isNew && flow.isUpdated);

            if (changedProcessFlows.length === 0) {
                // console.log('No process flows to save');
                setSaving(false);
                return;
            }

            const apiData = transformComponentToApi(changedProcessFlows);
            const payload = {
                sponsorId: sponsorId,
                ecosystemId: localStorage.getItem("ecosystemId"),
                processFlowsList: apiData.map(flow => ({
                    processFlowID: flow.processFlowID,
                    processFlowName: flow.processFlowName,
                    qaCheckRequired: flow.qaCheckRequired,
                    cmoCountry: flow.cmoCountry,
                    addresseeOrgName: flow.addresseeOrgName,
                    addresseeCountry: flow.addresseeCountry,
                    activated: flow.activated,
                    processFlowType: flow.processFlowType || 'Default',
                }))
            };

            // console.log('Saving process flows payload:', payload);

            const response = await axios.post(`${apiBaseUrl}/updateProcessFlows`, payload);

            if (response.data && response.data.status) {
                // console.log('Process flows updated successfully');
                // Refresh the data after successful save
                const fetchResponse = await axios.get(`${apiBaseUrl}/fetchAllProcessFlows/false`);
                if (fetchResponse.data && fetchResponse.data.status && fetchResponse.data.processFlowsList) {
                    const transformedData = transformApiToComponent(fetchResponse.data.processFlowsList);
                    dispatch({ type: 'SET_PROCESS_FLOWS', payload: transformedData });
                }
                // Notify parent component to update the count
                if (setProcessFlow) {
                    setProcessFlow(true);
                }
            } else {
                console.error('Failed to update process flows:', response.data?.message);
                setErrorMessage(response.data?.message || "Failed to update process flows");
            }
        } catch (e) {
            console.error('Error updating process flows:', e);
            setErrorMessage("An error occurred while updating process flows");
        } finally {
            setSaving(false);
        }
    };

    const handleResetAll = async () => {
        if (!apiBaseUrl) return;

        setIsLoading(true);
        try {
            const response = await axios.get(`${apiBaseUrl}/fetchAllProcessFlows/false`);
            if (response.data && response.data.status && response.data.processFlowsList) {
                const transformedData = transformApiToComponent(response.data.processFlowsList);
                dispatch({ type: 'SET_PROCESS_FLOWS', payload: transformedData });
            }
        } catch (e) {
            console.error('Error resetting process flows:', e);
        } finally {
            setIsLoading(false);
        }
    };

    // Helper function to get brand owner ID from token
    const getBrandOwnerMosymphonyID = () => {
        const token = localStorage.getItem('authToken');
        if (!token) return null;
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(c => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
                    .join('')
            );
            const payload = JSON.parse(jsonPayload);
            return payload.organization_id;
        } catch {
            return null;
        }
    };

    // Check if any new row has empty required fields
    const disableSaveSection =
        !state.isUpdated || hasEmptyRequiredFields(state.processFlows);

    return (
        <Box display="flex" alignItems="flex-start" gap={4}>
            <Box flex={1}>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow className={classes.tableHeader}>
                                {[
                                    'Selection',
                                    'Process Flow',
                                    'Process Flow ID',
                                    'Process Flow Mapping',
                                    'Version History',
                                    'QA Check Req.',
                                    'Vendor - Destination Location',
                                    'Action',
                                ].map((header) => (
                                    <TableCell key={header} className={classes.headerCell}>{header}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" style={{ padding: '40px 16px' }}>
                                        <CircularProgress size={40} />
                                    </TableCell>
                                </TableRow>
                            ) : state.processFlows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography>No process flows found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                state.processFlows.map((row, idx) => (
                                    <TableRow key={row.id + idx}>
                                        <TableCell>
                                            <Switch disabled={!canEdit} checked={row.selected} onChange={() => handleToggle(idx)} classes={{ switchBase: classes.switchBase }} color="primary" />
                                        </TableCell>
                                        <TableCell>
                                            {row.isNew ? (
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    {row.isEditing?.name ? (
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <TextField
                                                                value={row.name || ''}
                                                                onChange={e => handleFieldChange(idx, 'name', e.target.value)}
                                                                onBlur={(e) => {
                                                                    const { value } = e.target;
                                                                    // Trim leading and trailing spaces for string values
                                                                    if (typeof value === "string" && value.trim() !== value) {
                                                                        handleFieldChange(idx, 'name', value.trim());
                                                                    }
                                                                }}
                                                                placeholder="Process Flow Name"
                                                                size="small"
                                                                variant="outlined"
                                                                className={classes.textField}
                                                            />
                                                            <div className={classes.inlineActions}>
                                                                <IconButton 
                                                                    onClick={() => handleCancelField(idx, 'name')} 
                                                                    className={classes.iconBtn}
                                                                >
                                                                    <CancelIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    onClick={() => handleSaveField(idx, 'name')}
                                                                    className={classes.iconBtn}
                                                                    disabled={
                                                                        !row.name?.trim() ||
                                                                        savingStates[`${idx}-name`] ||
                                                                        state.processFlows.some(
                                                                            (flow, i) =>
                                                                                i !== idx &&
                                                                                flow.name?.trim().toLowerCase() === row.name?.trim().toLowerCase()
                                                                        )
                                                                    }
                                                                >
                                                                    <SaveIcon fontSize="small" />
                                                                </IconButton>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            {row.name || <span style={{ color: '#aaa' }}>Process Flow Name</span>}
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => handleSetEditing(idx, 'name')} 
                                                                className={classes.pencil}
                                                                disabled={!canEdit}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                row.name
                                            )}
                                        </TableCell>
                                        <TableCell>{row.id}</TableCell>
                                        <TableCell><span className={classes.link}>{row.mapping}</span></TableCell>
                                        <TableCell><span >{row.version}</span></TableCell>
                                        <TableCell>
                                            {row.isNew ? (
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    {row.isEditing?.qaCheck ? (
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <TextField
                                                                value={row.qaCheck || ''}
                                                                onChange={e => {
                                                                    const value = e.target.value;
                                                                    handleFieldChange(idx, 'qaCheck', value);
                                                                }}
                                                                onBlur={(e) => {
                                                                    const { value } = e.target;
                                                                    // Trim leading and trailing spaces for string values
                                                                    if (typeof value === "string" && value.trim() !== value) {
                                                                        handleFieldChange(idx, 'qaCheck', value.trim());
                                                                    }
                                                                }}
                                                                placeholder="Yes/No"
                                                                size="small"
                                                                variant="outlined"
                                                                style={{ width: 100 }}
                                                            />
                                                            <div className={classes.inlineActions}>
                                                                <IconButton 
                                                                    onClick={() => handleCancelField(idx, 'qaCheck')} 
                                                                    className={classes.iconBtn}
                                                                >
                                                                    <CancelIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    onClick={() => handleSaveField(idx, 'qaCheck')}
                                                                    className={classes.iconBtn}
                                                                    disabled={
                                                                        !row.qaCheck?.trim() ||
                                                                        savingStates[`${idx}-qaCheck`] ||
                                                                        !["Yes", "No"].includes(row.qaCheck)
                                                                    }
                                                                >
                                                                    <SaveIcon fontSize="small" />
                                                                </IconButton>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            {row.qaCheck || <span style={{ color: '#aaa' }}>Yes/No</span>}
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => handleSetEditing(idx, 'qaCheck')} 
                                                                className={classes.pencil}
                                                                disabled={!canEdit}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                row.qaCheck
                                            )}
                                        </TableCell>
                                        <TableCell className={classes.vendorCell}>
                                            {row.isNew ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        {row.isEditing?.cmoCountry ? (
                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                <TextField
                                                                    value={row.cmoCountry || ''}
                                                                    onChange={e => handleFieldChange(idx, 'cmoCountry', e.target.value)}
                                                                    onBlur={(e) => {
                                                                        const { value } = e.target;
                                                                        // Trim leading and trailing spaces for string values
                                                                        if (typeof value === "string" && value.trim() !== value) {
                                                                            handleFieldChange(idx, 'cmoCountry', value.trim());
                                                                        }
                                                                    }}
                                                                    placeholder="CMO Country"
                                                                    size="small"
                                                                    variant="outlined"
                                                                    style={{ width: 120 }}
                                                                />
                                                                <div className={classes.inlineActions}>
                                                                    <IconButton 
                                                                        onClick={() => handleCancelField(idx, 'cmoCountry')} 
                                                                        className={classes.iconBtn}
                                                                    >
                                                                        <CancelIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton 
                                                                        onClick={() => handleSaveField(idx, 'cmoCountry')}
                                                                        className={classes.iconBtn}
                                                                        disabled={
                                                                            !row.cmoCountry?.trim() ||
                                                                            savingStates[`${idx}-cmoCountry`]
                                                                        }
                                                                    >
                                                                        <SaveIcon fontSize="small" />
                                                                    </IconButton>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                {row.cmoCountry || <span style={{ color: '#aaa' }}>CMO Country</span>}
                                                                <IconButton 
                                                                    size="small" 
                                                                    onClick={() => handleSetEditing(idx, 'cmoCountry')} 
                                                                    className={classes.pencil}
                                                                    disabled={!canEdit}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        {row.isEditing?.addresseeOrgName ? (
                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                <TextField
                                                                    value={row.addresseeOrgName || ''}
                                                                    onChange={e => handleFieldChange(idx, 'addresseeOrgName', e.target.value)}
                                                                    onBlur={(e) => {
                                                                        const { value } = e.target;
                                                                        // Trim leading and trailing spaces for string values
                                                                        if (typeof value === "string" && value.trim() !== value) {
                                                                            handleFieldChange(idx, 'addresseeOrgName', value.trim());
                                                                        }
                                                                    }}
                                                                    placeholder="Addressee Org Name"
                                                                    size="small"
                                                                    variant="outlined"
                                                                    style={{ width: 150 }}
                                                                />
                                                                <div className={classes.inlineActions}>
                                                                    <IconButton 
                                                                        onClick={() => handleCancelField(idx, 'addresseeOrgName')} 
                                                                        className={classes.iconBtn}
                                                                    >
                                                                        <CancelIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton 
                                                                        onClick={() => handleSaveField(idx, 'addresseeOrgName')}
                                                                        className={classes.iconBtn}
                                                                        disabled={
                                                                            !row.addresseeOrgName?.trim() ||
                                                                            savingStates[`${idx}-addresseeOrgName`]
                                                                        }
                                                                    >
                                                                        <SaveIcon fontSize="small" />
                                                                    </IconButton>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                {row.addresseeOrgName || <span style={{ color: '#aaa' }}>Addressee Org Name</span>}
                                                                <IconButton 
                                                                    size="small" 
                                                                    onClick={() => handleSetEditing(idx, 'addresseeOrgName')} 
                                                                    className={classes.pencil}
                                                                    disabled={!canEdit}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        {row.isEditing?.addresseeCountry ? (
                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                <TextField
                                                                    value={row.addresseeCountry || ''}
                                                                    onChange={e => handleFieldChange(idx, 'addresseeCountry', e.target.value)}
                                                                    onBlur={(e) => {
                                                                        const { value } = e.target;
                                                                        // Trim leading and trailing spaces for string values
                                                                        if (typeof value === "string" && value.trim() !== value) {
                                                                            handleFieldChange(idx, 'addresseeCountry', value.trim());
                                                                        }
                                                                    }}
                                                                    placeholder="Addressee Country"
                                                                    size="small"
                                                                    variant="outlined"
                                                                    style={{ width: 120 }}
                                                                />
                                                                <div className={classes.inlineActions}>
                                                                    <IconButton 
                                                                        onClick={() => handleCancelField(idx, 'addresseeCountry')} 
                                                                        className={classes.iconBtn}
                                                                    >
                                                                        <CancelIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton 
                                                                        onClick={() => handleSaveField(idx, 'addresseeCountry')}
                                                                        className={classes.iconBtn}
                                                                        disabled={
                                                                            !row.addresseeCountry?.trim() ||
                                                                            savingStates[`${idx}-addresseeCountry`]
                                                                        }
                                                                    >
                                                                        <SaveIcon fontSize="small" />
                                                                    </IconButton>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                {row.addresseeCountry || <span style={{ color: '#aaa' }}>Addressee Country</span>}
                                                                <IconButton 
                                                                    size="small" 
                                                                    onClick={() => handleSetEditing(idx, 'addresseeCountry')} 
                                                                    className={classes.pencil}
                                                                    disabled={!canEdit}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                row.vendor.map((v, vIdx) => (
                                                    <div key={vIdx} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                                                        {v}
                                                    </div>
                                                ))
                                            )}
                                        </TableCell>
                                        <TableCell className={classes.actionCell}>
                                            {canEdit && (row.isNew || row.processFlowType === "New") && (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                    <IconButton 
                                                        aria-label="delete" 
                                                        onClick={() => handleDeleteRow(idx)}
                                                        className={classes.iconBtn}
                                                    >
                                                        <DeleteIcon className={classes.deleteIcon} />
                                                    </IconButton>
                                                    {row.isNew && (
                                                        <IconButton 
                                                            aria-label="save row" 
                                                            onClick={() => handleSaveRow(idx)}
                                                            className={classes.iconBtn}
                                                            disabled={
                                                            !row.name?.trim() ||
                                                            !row.qaCheck?.trim() ||
                                                            !["Yes", "No"].includes(row.qaCheck) ||
                                                            !row.cmoCountry?.trim() ||
                                                            !row.addresseeOrgName?.trim() ||
                                                            !row.addresseeCountry?.trim() ||
                                                            savingStates[idx] ||
                                                            state.processFlows.some(
                                                                (flow, i) =>
                                                                    i !== idx &&
                                                                    flow.name?.trim().toLowerCase() === row.name?.trim().toLowerCase()
                                                            )
                                                        }
                                                        >
                                                            <SaveIcon fontSize="small" style={{ color: '#f26522' }} />
                                                        </IconButton>
                                                    )}
                                                </div>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {canEdit && (
                  <Button
                      className={classes.addButton}
                      onClick={handleAddNew}
                      startIcon={<AddCircleOutlineIcon />}
                  >
                      Add New Process Flow
                  </Button>
                )}
                {/* Copy Existing Process Flow Dialog */}
                <Dialog
                    open={copyDialogOpen}
                    onClose={() => setCopyDialogOpen(false)}
                    maxWidth="xs"
                    fullWidth
                    classes={{ paper: dialogClasses.dialogPaper }}
                >
                    <DialogTitle className={dialogClasses.dialogTitle}>
                        <span style={{ fontWeight: 700, fontSize: 22, display: 'block', textAlign: 'center' }}>
                            Copy Existing Process Flow
                        </span>
                    </DialogTitle>
                    <DialogContent className={dialogClasses.dialogContent} style={{ paddingTop: 24 }}>
                        <Typography style={{ marginBottom: 16, textAlign: 'center' }}>
                            Following is the list of existing process flows.<br />
                            Please select a process flow if you want to use it as a starting point for the new process flow mapping:
                        </Typography>
                        <div className={dialogClasses.scrollList}>
                            <FormControl component="fieldset" style={{ width: '100%' }}>
                                <RadioGroup
                                    value={copySelected}
                                    onChange={e => setCopySelected(e.target.value)}
                                >
                                    {existingFlows.map(flow => (
                                        <FormControlLabel
                                            key={flow.id}
                                            value={flow.id}
                                            control={<Radio color="primary" />}
                                            label={flow.name}
                                        />
                                    ))}
                                    <FormControlLabel
                                        value="none"
                                        control={<Radio color="primary" />}
                                        label="None of the above"
                                    />
                                </RadioGroup>
                            </FormControl>
                        </div>
                    </DialogContent>
                    <DialogActions style={{ justifyContent: 'center', paddingBottom: 24 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCopyContinue}
                            style={{ background: '#f26522', color: '#fff', borderRadius: 20, padding: '6px 24px', textTransform: 'none', fontWeight: 600 }}
                        >
                            Continue
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
            <div
                className={classes.footer}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "16px",
                    marginTop: "16px",
                    marginLeft: "16px",
                }}
            >
                <Button
                    className={classes.saveButton}
                    type="button"
                    onClick={handleSaveAll}
                    disabled={disableSaveSection || saving || isLoading || !canEdit}
                    style={{
                        opacity: disableSaveSection || saving || isLoading || !canEdit ? 0.7 : 1,
                        color: "#fff",
                    }}
                >
                    {saving ? (
                        <>
                            <CircularProgress size={20} color="inherit" style={{ marginRight: 8 }} />
                            Saving...
                        </>
                    ) : (
                        'Save Selection'
                    )}
                </Button>
                <Button
                    className={classes.resetButton}
                    type="button"
                    onClick={handleResetAll}
                    disabled={!state.isUpdated || isLoading || !canEdit}
                    style={{
                        opacity: !state.isUpdated || isLoading || !canEdit ? 0.8 : 1,
                    }}
                >
                    Reset Selection
                </Button>
            </div>
        </Box>
    );
};

export default ProcessFlowTab; 